import React, { useMemo } from 'react';

import { Group, Radio, Stack, Text, Title } from '@mantine/core';
import { IconInfoSquareRounded } from '@tabler/icons-react';
import KiloActionIcon from 'components/KiloActionIcon';
import KiloTooltip, { TOOLTIP_MESSAGES } from 'components/KiloTooltip';
import useAutoCompleteProduct from 'hooks/product/useAutoCompleteProduct';

import ProductSelectModalField from './ProductSelectModalField';

export type EligibleProductsType = 'ALL' | 'SPECIFIC';

interface IEligibleProductsSectionProps {
  productsType: EligibleProductsType;
  selectedProducts: string[];
  onTypeChange: (value: EligibleProductsType) => void;
  onSelectedProductsChange: (value: string[]) => void;
  disabled?: boolean;
  error?: string | null;
}

const EligibleProductsSection = ({
  productsType,
  selectedProducts,
  onTypeChange,
  onSelectedProductsChange,
  disabled = false,
  error,
}: IEligibleProductsSectionProps): JSX.Element => {
  const { data: packageData } = useAutoCompleteProduct({ type: 'PACKAGE' });
  const { data: retailData } = useAutoCompleteProduct({ type: 'RETAIL' });

  const packageIds = useMemo(() => new Set((packageData?.data ?? []).map(p => p.productId)), [packageData]);
  const retailIds = useMemo(() => new Set((retailData?.data ?? []).map(p => p.productId)), [retailData]);

  const selectedPackages = selectedProducts.filter(id => packageIds.has(id));
  const selectedRetail = selectedProducts.filter(id => retailIds.has(id));

  const handlePackagesChange = (newPackages: string[]): void => {
    onSelectedProductsChange([...newPackages, ...selectedRetail]);
  };

  const handleRetailChange = (newRetail: string[]): void => {
    onSelectedProductsChange([...selectedPackages, ...newRetail]);
  };

  return (
    <Stack>
      <Group noWrap align='flex-start' position='apart'>
        <Title order={3}>2. Eligible Products</Title>
        <KiloTooltip
          wrapperDivClassVariantType='default'
          label={TOOLTIP_MESSAGES.DISCOUNT_ELIGIBLE_PRODUCTS}
          withArrow
          position='right-start'
        >
          <KiloActionIcon rootVariantType='default' variant='subtle'>
            <IconInfoSquareRounded size='1.4rem' />
          </KiloActionIcon>
        </KiloTooltip>
      </Group>

      <Text size='xs' color='gray'>
        Choose which product types and items can use this discount
      </Text>

      <Radio.Group value={productsType} onChange={value => onTypeChange(value as EligibleProductsType)}>
        <Group spacing='xs' mt='xs'>
          <Radio value='ALL' label='All Products' disabled={disabled} />
          <Radio value='SPECIFIC' label='Specific Products' disabled={disabled} />
        </Group>
      </Radio.Group>

      {productsType === 'SPECIFIC' && (
        <Stack>
          <Text size='sm'>Only selected products are included. New ones won’t be added automatically.</Text>
          <ProductSelectModalField
            label='Packages'
            type='PACKAGE'
            value={selectedPackages}
            onChange={handlePackagesChange}
            disabled={disabled}
          />
          <ProductSelectModalField
            label='Retail'
            type='RETAIL'
            value={selectedRetail}
            onChange={handleRetailChange}
            disabled={disabled}
          />
          {error && (
            <Text color='red' size='xs'>
              {error}
            </Text>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default EligibleProductsSection;
